import { useRef, useCallback } from 'react'
import { LocaleTextHook } from '@moonpig/web-core-locale-text'
import { DepartmentType } from '../departmentMap'
import { NavigationalSuggestionType } from '../NavigationalSuggestions/NavigationalSuggestion'
import { Reminder } from '../Reminders/Reminder'
import { buildSearchRows } from '../searchRows'
import type { SearchSections } from '../searchRows/types'
import type {
  InspirationType,
  RecentSearchType,
  SearchRow,
  SuggestionType,
} from '../types'
import { NAV_TEXT } from '../../locale'

export type UseCachedSearchRowsParams = {
  departments: DepartmentType[]
  activeDepartment: DepartmentType
  items: SuggestionType[]
  navigationalSuggestions: NavigationalSuggestionType[]
  recentSearches: RecentSearchType[]
  inspirations: InspirationType[]
  reminders: Reminder[]
  localiseText: LocaleTextHook<typeof NAV_TEXT>
  maxSuggestions: number
  maxNavigationalSuggestions: number
}

export const useCachedSearchRows = (
  params: UseCachedSearchRowsParams,
): ((inputValue: string | null) => {
  sections: SearchSections
  flatList: SearchRow[]
}) => {
  const {
    departments,
    activeDepartment,
    items,
    navigationalSuggestions,
    recentSearches,
    inspirations,
    reminders,
    localiseText,
    maxSuggestions,
    maxNavigationalSuggestions,
  } = params

  const cacheRef = useRef<{
    sections: SearchSections
    flatList: SearchRow[]
    inputValue: string | null
    items: SuggestionType[]
    activeDepartment: DepartmentType
    navigationalSuggestions: NavigationalSuggestionType[]
    recentSearches: RecentSearchType[]
    inspirations: InspirationType[]
    reminders: Reminder[]
    maxSuggestions: number
    maxNavigationalSuggestions: number
  } | null>(null)

  return useCallback(
    (inputValue: string | null) => {
      const cache = cacheRef.current
      const cacheHit =
        cache &&
        cache.inputValue === inputValue &&
        cache.items === items &&
        cache.activeDepartment === activeDepartment &&
        cache.navigationalSuggestions === navigationalSuggestions &&
        cache.recentSearches === recentSearches &&
        cache.inspirations === inspirations &&
        cache.reminders === reminders &&
        cache.maxSuggestions === maxSuggestions &&
        cache.maxNavigationalSuggestions === maxNavigationalSuggestions

      if (cacheHit) {
        return {
          sections: cache.sections,
          flatList: cache.flatList,
        }
      }

      const { sections, flatList } = buildSearchRows({
        inputValue,
        departments,
        activeDepartment,
        suggestions: items,
        navigationalSuggestions,
        recentSearches,
        inspirations,
        reminders,
        localiseText,
        maxSuggestions,
        maxNavigationalSuggestions,
      })

      cacheRef.current = {
        sections: {
          departmentRows: sections.departmentRows,
          focusableRows: sections.focusableRows,
          autocompleteRows: sections.autocompleteRows,
        },
        flatList,
        inputValue,
        items,
        activeDepartment,
        navigationalSuggestions,
        recentSearches,
        inspirations,
        reminders,
        maxSuggestions,
        maxNavigationalSuggestions,
      }

      return { sections, flatList }
    },
    [
      items,
      activeDepartment,
      navigationalSuggestions,
      recentSearches,
      inspirations,
      reminders,
      maxSuggestions,
      maxNavigationalSuggestions,
      departments,
      localiseText,
    ],
  )
}
